import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Layout from '../../components/Layout';
import api from '../../api/axios';
import { confirm } from '../../components/ConfirmDialog';
import {
    CANVAS_H, CANVAS_W, FloorLabel, FloorShape, isPlaced, parsePoints, readableText, sortLayers, tableBoxStyle, useCanvasScale,
} from '../../components/FloorPlanView';
import { alignItems, boxOf, buildGuides, distributeItems, shiftBox, snapDelta, unionBox } from '../../utils/floorSnap';

const SNAP = 10;
const OVERLAY_Z = 1000; // çərçivə və xətlər bütün layerlərin üstündə
const GUIDE = '#e11d8f';
const DRAG_START = 5; // Ctrl+klik ilə Ctrl+çəkməni ayırmaq üçün
const MIN_SIZE = 40;
const MAX_TABLE_SIZE = 600;
const CELL = { w: 170, h: 150 };
const NO_ROOM = 'none';
const OVERVIEW = 'overview'; // «Ümumi plan» tabı: zal düymələri və forması
const MAX_SHAPES = 200;
const MAX_HISTORY = 100;
const PASTE_OFFSET = 20;
const COALESCE_MS = 800; // yazı və rəqəm sahələrində ardıcıl dəyişiklik bir addım sayılır
const DECOR_COLORS = ['#bae6fd', '#bbf7d0', '#fde68a', '#fecaca', '#ddd6fe', '#e7e5e4', '#475569'];

const POLYGONS = {
    hexagon: [[25, 0], [75, 0], [100, 50], [75, 100], [25, 100], [0, 50]],
    l: [[0, 0], [45, 0], [45, 55], [100, 55], [100, 100], [0, 100]],
    u: [[0, 0], [30, 0], [30, 60], [70, 60], [70, 0], [100, 0], [100, 100], [0, 100]],
    t: [[0, 0], [100, 0], [100, 40], [65, 40], [65, 100], [35, 100], [35, 40], [0, 40]],
};

const SHAPE_OPTIONS = [
    { key: 'round', label: 'Yumru', patch: () => ({ fp_shape: 'circle', fp_w: 120, fp_h: 120, fp_points: null }) },
    { key: 'oval', label: 'Oval', patch: () => ({ fp_shape: 'circle', fp_w: 200, fp_h: 110, fp_points: null }) },
    { key: 'square', label: 'Kvadrat', patch: () => ({ fp_shape: 'rect', fp_w: 120, fp_h: 120, fp_points: null }) },
    { key: 'rect', label: 'Düzbucaqlı', patch: () => ({ fp_shape: 'rect', fp_w: 220, fp_h: 110, fp_points: null }) },
    { key: 'rounded', label: 'Yumru künc', patch: () => ({ fp_shape: 'rounded', fp_w: 200, fp_h: 120, fp_points: null }) },
    { key: 'hexagon', label: 'Altıbucaq', patch: () => ({ fp_shape: 'polygon', fp_w: 150, fp_h: 130, fp_points: POLYGONS.hexagon }) },
    { key: 'l', label: 'L forması', patch: () => ({ fp_shape: 'polygon', fp_w: 220, fp_h: 180, fp_points: POLYGONS.l }) },
    { key: 'u', label: 'U forması', patch: () => ({ fp_shape: 'polygon', fp_w: 240, fp_h: 180, fp_points: POLYGONS.u }) },
    { key: 't', label: 'T forması', patch: () => ({ fp_shape: 'polygon', fp_w: 220, fp_h: 180, fp_points: POLYGONS.t }) },
    { key: 'custom', label: 'Xüsusi forma', patch: (t) => ({ fp_shape: 'polygon', fp_points: parsePoints(t.fp_points) || [[0, 0], [100, 0], [100, 100], [0, 100]] }) },
];

const activeShapeKey = (t) => {
    if (t.fp_shape === 'circle') return t.fp_w === t.fp_h ? 'round' : 'oval';
    if (t.fp_shape === 'rounded') return 'rounded';
    if (t.fp_shape === 'polygon') {
        const json = JSON.stringify(parsePoints(t.fp_points));
        return Object.keys(POLYGONS).find((k) => JSON.stringify(POLYGONS[k]) === json) || 'custom';
    }
    return t.fp_w === t.fp_h ? 'square' : 'rect';
};

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const snap = (v) => Math.round(v / SNAP) * SNAP;
const maxW = (it) => (it.kind === 'table' ? MAX_TABLE_SIZE : CANVAS_W);
const maxH = (it) => (it.kind === 'table' ? MAX_TABLE_SIZE : CANVAS_H);
const itemName = (t) => (t.kind === 'table' ? t.name : (t.label || t.name || 'Forma'));
const clampBox = (t) => (isPlaced(t) ? {
    ...t,
    fp_x: clamp(t.fp_x, 0, CANVAS_W - t.fp_w),
    fp_y: clamp(t.fp_y, 0, CANVAS_H - t.fp_h),
} : t);
const rotateVec = (x, y, deg) => {
    const r = (deg * Math.PI) / 180;
    return [x * Math.cos(r) - y * Math.sin(r), x * Math.sin(r) + y * Math.cos(r)];
};

const roomKey = (t) => {
    if (Number(t.fp_overview)) return OVERVIEW;
    return t.room_id ? String(t.room_id) : NO_ROOM;
};
const overlaps = (a, x1, y1, x2, y2) => a.fp_x < x2 && a.fp_x + a.fp_w > x1 && a.fp_y < y2 && a.fp_y + a.fp_h > y1;

// Bu otaqda heç nə ilə üst-üstə düşməyən ilk yer: əvvəl geniş xanalar, sonra sıx axtarış.
// Yer qalmayıbsa pilləli qoyulur ki, masalar tam üst-üstə düşüb gözdən itməsin.
const findFreeCell = (placed, w, h) => {
    for (const [stepX, stepY] of [[CELL.w, CELL.h], [SNAP * 2, SNAP * 2]]) {
        for (let y = 20; y + h <= CANVAS_H; y += stepY) {
            for (let x = 20; x + w <= CANVAS_W; x += stepX) {
                if (!placed.some((t) => overlaps(t, x, y, x + w, y + h))) return { fp_x: x, fp_y: y };
            }
        }
    }
    const step = placed.length % 20;
    return { fp_x: 20 + step * 30, fp_y: 20 + step * 30 };
};

const ALIGN_BUTTONS = [
    { mode: 'left', label: '⇤ Sol' },
    { mode: 'hcenter', label: '↔ Mərkəz' },
    { mode: 'right', label: 'Sağ ⇥' },
    { mode: 'top', label: '⤒ Yuxarı' },
    { mode: 'vcenter', label: '↕ Orta' },
    { mode: 'bottom', label: '⤓ Aşağı' },
];

// Ölçü tutacaqları: 4 künc və 4 tərəf. hx/hy: hansı tərəf çəkilir (0 = o ox dəyişmir)
const RESIZE_HANDLES = [
    { hx: -1, hy: -1, left: '0%', top: '0%', cursor: 'nwse-resize' },
    { hx: 0, hy: -1, left: '50%', top: '0%', cursor: 'ns-resize' },
    { hx: 1, hy: -1, left: '100%', top: '0%', cursor: 'nesw-resize' },
    { hx: 1, hy: 0, left: '100%', top: '50%', cursor: 'ew-resize' },
    { hx: 1, hy: 1, left: '100%', top: '100%', cursor: 'nwse-resize' },
    { hx: 0, hy: 1, left: '50%', top: '100%', cursor: 'ns-resize' },
    { hx: -1, hy: 1, left: '0%', top: '100%', cursor: 'nesw-resize' },
    { hx: -1, hy: 0, left: '0%', top: '50%', cursor: 'ew-resize' },
];

// Yazarkən aralıq rəqəmlər (300 üçün 3, 30) sərhədə çəkilmir: yalnız düzgün dəyər tətbiq olunur,
// çıxanda və ya Enter-də qalan dəyər sərhədə gətirilir. Boş buraxılsa əvvəlki dəyər qalır.
const primaryKind = (keys, list) => list.find((t) => t.key === keys[0])?.kind;

const NumberField = ({ label, value, min, max, onChange }) => {
    const [draft, setDraft] = useState(null);

    const handleChange = (e) => {
        const raw = e.target.value;
        setDraft(raw);
        const n = Number(raw);
        if (raw !== '' && n >= min && n <= max) onChange(Math.round(n));
    };

    const commit = () => {
        if (draft === null) return;
        const n = Number(draft);
        if (draft !== '' && Number.isFinite(n)) onChange(clamp(Math.round(n), min, max));
        setDraft(null);
    };

    return (
        <label className="block">
            <span className="mb-1 block text-xs font-semibold text-gray-500">{label}</span>
            <input
                type="number"
                value={draft ?? value}
                min={min}
                max={max}
                onChange={handleChange}
                onBlur={commit}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                className="w-full rounded-xl border-2 border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
            />
        </label>
    );
};

const FloorPlan = () => {
    const navigate = useNavigate();
    // Masalar və dekor formaları bir siyahıdadır: kind 'table' | 'decor', key 't5' | 'd3'
    const [items, setItems] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [enabled, setEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [picked, setPicked] = useState('');
    const [selectedKeys, setSelectedKeys] = useState([]); // birinci seçilən = əsas (ölçü və forma nümunəsi)
    const [selectedVertex, setSelectedVertex] = useState(null);
    const [marquee, setMarquee] = useState(null);
    const [guides, setGuides] = useState(null);
    const [layerDrag, setLayerDrag] = useState(null); // { key, over, after }
    const canvasRef = useRef(null);
    const dragRef = useRef(null);
    const newDecorId = useRef(0);
    const clipboardRef = useRef({ items: [], pastes: 0 }); // Ctrl+C ilə kopyalanan formalar
    const itemsRef = useRef(items);
    itemsRef.current = items;
    const historyRef = useRef({ undo: [], redo: [], tag: null, at: 0 });
    const [historySize, setHistorySize] = useState({ undo: 0, redo: 0 });
    const scale = useCanvasScale(canvasRef);

    useEffect(() => {
        Promise.all([api.get('/tables/all'), api.get('/rooms/all')])
            .then(([tabRes, roomRes]) => {
                const roomList = (roomRes.data.data || []).filter((r) => Number(r.active) === 1 && Number(r.is_takeaway || 0) === 0);
                const takeawayIds = new Set((roomRes.data.data || []).filter((r) => Number(r.is_takeaway || 0) === 1).map((r) => r.id));
                const tableItems = (tabRes.data.data || [])
                    .filter((t) => Number(t.active) === 1 && !takeawayIds.has(t.room_id))
                    .map((t) => ({ ...t, kind: 'table', key: `t${t.id}`, fp_points: parsePoints(t.fp_points) }));
                const shapes = tabRes.data.floor_shapes || [];
                const decorItems = shapes.filter((d) => !d.link_room)
                    .map((d) => ({ ...d, kind: 'decor', key: `d${d.id}`, fp_points: parsePoints(d.fp_points) }));

                // Ümumi plandakı zal düymələri: aktiv otaqlar + masası olan passiv/otaqsız bölmələr
                const roomDefs = new Map((roomRes.data.data || []).filter((r) => Number(r.active) === 1).map((r) => [String(r.id), r.name]));
                (tabRes.data.data || []).filter((t) => Number(t.active) === 1).forEach((t) => {
                    const key = t.room_id ? String(t.room_id) : NO_ROOM;
                    if (!roomDefs.has(key)) roomDefs.set(key, t.room_name || 'Otaqsız');
                });
                const roomItems = [...roomDefs].map(([link, name]) => {
                    const saved = shapes.find((d) => d.link_room === link);
                    return {
                        kind: 'room',
                        key: `r${link}`,
                        link_room: link,
                        name,
                        room_id: null,
                        fp_overview: 1,
                        fp_x: saved?.fp_x ?? null,
                        fp_y: saved?.fp_y ?? null,
                        fp_w: saved?.fp_w ?? 220,
                        fp_h: saved?.fp_h ?? 120,
                        fp_rot: saved?.fp_rot ?? 0,
                        fp_shape: saved?.fp_shape ?? 'rounded',
                        fp_points: parsePoints(saved?.fp_points),
                        fp_group: saved?.fp_group ?? null,
                        fp_z: saved?.fp_z ?? 0,
                        fill: saved?.fill ?? '#e0f4fb',
                        label: saved?.label ?? '',
                    };
                });
                setRooms(roomList);
                setItems([...tableItems, ...decorItems, ...roomItems]);
                setEnabled(!!tabRes.data.floor_plan);
            })
            .catch(() => toast.error('Masalar yüklənmədi.'))
            .finally(() => setLoading(false));
    }, []);

    const tabs = useMemo(() => {
        const list = [{ key: OVERVIEW, name: 'Ümumi plan' }, ...rooms.map((r) => ({ key: String(r.id), name: r.name }))];
        // Otağı olmayan və ya otağı passiv olan masalar da planda görünsün
        items.filter((t) => t.kind === 'table').forEach((t) => {
            const key = roomKey(t);
            if (!list.some((tab) => tab.key === key)) list.push({ key, name: t.room_name || 'Otaqsız' });
        });
        return list;
    }, [rooms, items]);

    const active = tabs.some((tab) => tab.key === picked) ? picked : tabs[0]?.key;

    const roomItems = items.filter((t) => roomKey(t) === active);
    const placed = roomItems.filter(isPlaced);
    const sameKind = placed.filter((t) => t.kind === primaryKind(selectedKeys, placed));
    const roomFields = active === OVERVIEW
        ? { room_id: null, fp_overview: 1 }
        : { room_id: active === NO_ROOM ? null : Number(active), fp_overview: 0 };
    const stacked = sortLayers(placed);
    const zOf = new Map(stacked.map((t, i) => [t.key, i + 1]));
    const layers = [...stacked].reverse(); // siyahıda yuxarıdakı = ön planda
    const topZ = Math.max(0, ...roomItems.map((t) => t.fp_z || 0));
    const unplaced = roomItems.filter((t) => !isPlaced(t));
    const selection = placed.filter((t) => selectedKeys.includes(t.key));
    const primary = placed.find((t) => t.key === selectedKeys[0]) || selection[0] || null;
    const single = selection.length === 1 ? primary : null;

    const syncHistorySize = () => {
        const h = historyRef.current;
        setHistorySize({ undo: h.undo.length, redo: h.redo.length });
    };

    // Dəyişiklikdən ƏVVƏL çağırılır: cari vəziyyət geri qaytarma siyahısına düşür
    const remember = (tag = null) => {
        const h = historyRef.current;
        const now = Date.now();
        const same = tag && h.tag === tag && now - h.at < COALESCE_MS;
        h.tag = tag;
        h.at = now;
        if (same) return;
        h.undo.push(itemsRef.current);
        if (h.undo.length > MAX_HISTORY) h.undo.shift();
        h.redo = [];
        syncHistorySize();
    };

    const travel = (from, to) => {
        const h = historyRef.current;
        if (!h[from].length) return;
        h[to].push(itemsRef.current);
        setItems(h[from].pop());
        h.tag = null;
        setDirty(true);
        setSelectedVertex(null);
        syncHistorySize();
    };
    const undo = () => travel('undo', 'redo');
    const redo = () => travel('redo', 'undo');

    // Çəkmə zamanı addım startda bir dəfə yazılır (handleMove), hər tərpənişdə yox
    const patchItems = (fn, tag) => {
        if (!dragRef.current) remember(tag);
        setDirty(true);
        setItems((prev) => prev.map((t) => {
            const patch = fn(t);
            return patch ? clampBox({ ...t, ...patch }) : t;
        }));
    };
    const patchKey = (key, patch, tag) => patchItems((t) => (t.key === key ? patch : null), tag);
    const patchSelection = (fn, tag) => patchItems((t) => (selectedKeys.includes(t.key) ? fn(t) : null), tag);

    const select = (keys) => { setSelectedKeys(keys); setSelectedVertex(null); };

    // Qrupun bir üzvü seçiləndə bütün qrup seçilir
    const withGroups = (keys) => {
        const ids = new Set(placed.filter((t) => keys.includes(t.key) && t.fp_group).map((t) => t.fp_group));
        return [...new Set([...keys, ...placed.filter((t) => ids.has(t.fp_group)).map((t) => t.key)])];
    };

    const groupSelection = () => {
        if (selection.length < 2) return;
        const id = `g${Date.now().toString(36)}`;
        patchItems((t) => (selectedKeys.includes(t.key) ? { fp_group: id } : null));
    };
    const ungroupSelection = () => patchItems((t) => (selectedKeys.includes(t.key) ? { fp_group: null } : null));

    const placeTable = (table) => {
        patchKey(table.key, { ...findFreeCell(placed, table.fp_w, table.fp_h), fp_z: topZ + 1 });
        select([table.key]);
    };

    const placeAll = () => {
        const done = [...placed];
        const positions = new Map();
        unplaced.forEach((t, i) => {
            const cell = { ...findFreeCell(done, t.fp_w, t.fp_h), fp_z: topZ + 1 + i };
            positions.set(t.key, cell);
            done.push({ ...t, ...cell });
        });
        patchItems((t) => positions.get(t.key) || null);
    };

    const addDecor = () => {
        if (items.filter((t) => t.kind === 'decor').length >= MAX_SHAPES) {
            toast.error(`Maksimum ${MAX_SHAPES} forma əlavə etmək olar.`);
            return;
        }
        newDecorId.current += 1;
        const key = `dn${newDecorId.current}`;
        const decor = {
            kind: 'decor',
            key,
            ...roomFields,
            fp_x: 500,
            fp_y: 300,
            fp_w: 200,
            fp_h: 200,
            fp_rot: 0,
            fp_shape: 'rect',
            fp_points: null,
            fp_z: topZ + 1,
            fill: DECOR_COLORS[0],
            label: '',
        };
        remember();
        setDirty(true);
        setItems((prev) => [...prev, decor]);
        select([key]);
    };

    // Yalnız dekor formaları kopyalanır: masa real obyektdir (sifariş, nömrə), ikisi ola bilməz
    const copySelection = () => {
        const shapes = selection.filter((t) => t.kind === 'decor');
        if (!shapes.length) {
            if (selection.length) toast.error('Masalar kopyalanmır, yalnız formalar.');
            return;
        }
        clipboardRef.current = { items: shapes, pastes: 0 };
        toast.success(shapes.length > 1 ? `${shapes.length} forma kopyalandı.` : 'Forma kopyalandı.');
    };

    // Yapışdırma həmişə açıq olan otağa gedir, ona görə başqa otağa da köçürmək olur
    const pasteShapes = () => {
        const clip = clipboardRef.current;
        if (!clip.items.length) return;
        if (items.filter((t) => t.kind === 'decor').length + clip.items.length > MAX_SHAPES) {
            toast.error(`Maksimum ${MAX_SHAPES} forma əlavə etmək olar.`);
            return;
        }
        clip.pastes += 1;
        const offset = clip.pastes * PASTE_OFFSET;
        const groupIds = new Map();
        const copies = clip.items.map((t, i) => {
            newDecorId.current += 1;
            if (t.fp_group && !groupIds.has(t.fp_group)) groupIds.set(t.fp_group, `g${Date.now().toString(36)}${groupIds.size}`);
            return clampBox({
                ...t,
                id: undefined,
                key: `dn${newDecorId.current}`,
                ...roomFields,
                fp_x: t.fp_x + offset,
                fp_y: t.fp_y + offset,
                fp_z: topZ + 1 + i,
                fp_group: t.fp_group ? groupIds.get(t.fp_group) : null,
            });
        });
        remember();
        setDirty(true);
        setItems((prev) => [...prev, ...copies]);
        select(copies.map((t) => t.key));
    };

    // Masa plandan çıxır (masa özü silinmir), dekor forması isə tam silinir
    const removeSelection = () => {
        remember();
        setDirty(true);
        setItems((prev) => prev
            .filter((t) => !(t.kind === 'decor' && selectedKeys.includes(t.key)))
            .map((t) => (selectedKeys.includes(t.key) ? { ...t, fp_x: null, fp_y: null, fp_group: null } : t)));
        select([]);
    };

    // Bütün seçilənlər (bir seçim varsa otaqdakı bütün masalar) əsas masanın ölçüsünü alır
    const applySameSize = (withShape = false) => {
        const { fp_w: w, fp_h: h, fp_shape: shape, fp_points: points } = primary;
        const keys = selection.length > 1 ? selectedKeys : sameKind.map((t) => t.key);
        patchItems((t) => (keys.includes(t.key) ? {
            fp_w: Math.min(w, maxW(t)),
            fp_h: Math.min(h, maxH(t)),
            ...(withShape ? { fp_shape: shape, fp_points: points } : {}),
        } : null));
    };

    const applyOffsets = (offsets) => {
        const map = new Map(offsets.map((o) => [o.key, o]));
        patchItems((t) => {
            const o = map.get(t.key);
            return o ? { fp_x: Math.round(t.fp_x + o.dx), fp_y: Math.round(t.fp_y + o.dy) } : null;
        });
    };

    // Layer sırası: list[0] ən öndə. fp_z yenidən 1..n yazılır
    const applyLayers = (list) => {
        const zByKey = new Map(list.map((t, i) => [t.key, list.length - i]));
        patchItems((t) => (zByKey.has(t.key) ? { fp_z: zByKey.get(t.key) } : null));
    };

    const dropLayer = (over, after) => {
        const key = layerDrag?.key;
        setLayerDrag(null);
        if (!key) return;
        const keys = selectedKeys.includes(key) ? selectedKeys : [key];
        if (keys.includes(over)) return;
        const moving = layers.filter((t) => keys.includes(t.key));
        const rest = layers.filter((t) => !keys.includes(t.key));
        const at = rest.findIndex((t) => t.key === over) + (after ? 1 : 0);
        applyLayers([...rest.slice(0, at), ...moving, ...rest.slice(at)]);
    };

    // dir: 'front' ən öndə, 'back' ən arxada, 'up' / 'down' bir addım
    const moveLayers = (dir) => {
        const isSel = (t) => selectedKeys.includes(t.key);
        if (!selection.length) return;
        if (dir === 'front') { applyLayers([...layers.filter(isSel), ...layers.filter((t) => !isSel(t))]); return; }
        if (dir === 'back') { applyLayers([...layers.filter((t) => !isSel(t)), ...layers.filter(isSel)]); return; }
        const list = [...layers];
        const swap = (i, j) => { [list[i], list[j]] = [list[j], list[i]]; };
        if (dir === 'up') {
            for (let i = 1; i < list.length; i += 1) if (isSel(list[i]) && !isSel(list[i - 1])) swap(i, i - 1);
        } else {
            for (let i = list.length - 2; i >= 0; i -= 1) if (isSel(list[i]) && !isSel(list[i + 1])) swap(i, i + 1);
        }
        applyLayers(list);
    };

    const handleLayerClick = (e, t) => {
        const own = e.altKey ? [t.key] : withGroups([t.key]);
        if (e.ctrlKey || e.metaKey) {
            select(own.every((k) => selectedKeys.includes(k))
                ? selectedKeys.filter((k) => !own.includes(k))
                : [...new Set([...selectedKeys, ...own])]);
        } else {
            select([t.key, ...own.filter((k) => k !== t.key)]);
        }
    };

    const toLogical = (e) => {
        const r = canvasRef.current.getBoundingClientRect();
        return [((e.clientX - r.left) / r.width) * CANVAS_W, ((e.clientY - r.top) / r.height) * CANVAS_H];
    };

    // Çəkmə: daşımaq, ölçü dəyişmək, xüsusi formanın nöqtəsi, çərçivə ilə seçmək
    const handleMove = (e) => {
        const d = dragRef.current;
        if (!d) return;
        const [px, py] = toLogical(e);
        const dx = px - d.px;
        const dy = py - d.py;
        const t = d.item;

        // Ctrl basılı başlayıb: tərpənməyibsə klikdir (seçim), tərpənibsə sərbəst daşıma
        if (d.toggleKeys) {
            if (Math.hypot(dx, dy) < DRAG_START) return;
            const keys = d.toggleKeys;
            d.toggleKeys = null;
            setSelectedKeys((prev) => [...new Set([...prev, ...keys])]);
        }

        if (d.mode !== 'marquee' && !d.before) {
            d.before = itemsRef.current;
            remember();
        }

        if (d.mode === 'marquee') {
            const x1 = Math.min(px, d.px);
            const y1 = Math.min(py, d.py);
            const x2 = Math.max(px, d.px);
            const y2 = Math.max(py, d.py);
            setMarquee({ x1, y1, x2, y2 });
            const hit = d.candidates.filter((c) => overlaps(c, x1, y1, x2, y2)).map((c) => c.key);
            select(withGroups([...new Set([...d.base, ...hit])]));
        } else if (d.mode === 'move') {
            const box = unionBox(d.group.map(boxOf));
            let mx = Math.round(dx);
            let my = Math.round(dy);
            // Ctrl: sərbəst. Əks halda yaxın masalara yapışır, yapışacaq yer yoxdursa şəbəkəyə
            if (!(e.ctrlKey || e.metaKey)) {
                const others = d.others.map(boxOf);
                const delta = snapDelta(box, others, dx, dy);
                mx = delta.x !== null ? Math.round(dx + delta.x) : snap(box.l + dx) - box.l;
                my = delta.y !== null ? Math.round(dy + delta.y) : snap(box.t + dy) - box.t;
            }
            // Qrup formasını saxlasın: sürüşmə ən kənardakı masaya görə məhdudlaşır
            d.group.forEach((o) => {
                mx = clamp(mx, -o.fp_x, CANVAS_W - o.fp_w - o.fp_x);
                my = clamp(my, -o.fp_y, CANVAS_H - o.fp_h - o.fp_y);
            });
            const origin = new Map(d.group.map((o) => [o.key, o]));
            patchItems((it) => {
                const o = origin.get(it.key);
                return o ? { fp_x: Math.round(o.fp_x + mx), fp_y: Math.round(o.fp_y + my) } : null;
            });
            setGuides(e.ctrlKey || e.metaKey ? null : buildGuides(shiftBox(box, mx, my), d.others.map(boxOf)));
        } else if (d.mode === 'resize') {
            // Masa döndürülübsə çəkmə onun öz oxlarına çevrilir, mərkəz də ona uyğun sürüşür
            const [lx, ly] = rotateVec(dx, dy, -t.fp_rot);
            const round = e.ctrlKey || e.metaKey ? Math.round : snap;
            const w = d.hx ? clamp(round(t.fp_w + d.hx * lx), MIN_SIZE, maxW(t)) : t.fp_w;
            const h = d.hy ? clamp(round(t.fp_h + d.hy * ly), MIN_SIZE, maxH(t)) : t.fp_h;
            // Qarşı tərəf yerində qalır: mərkəz çəkilən tərəfə doğru yarım sürüşür
            const [cx, cy] = rotateVec((d.hx * (w - t.fp_w)) / 2, (d.hy * (h - t.fp_h)) / 2, t.fp_rot);
            patchKey(t.key, {
                fp_w: w,
                fp_h: h,
                fp_x: Math.round(t.fp_x + (t.fp_w - w) / 2 + cx),
                fp_y: Math.round(t.fp_y + (t.fp_h - h) / 2 + cy),
            });
        } else if (d.mode === 'vertex') {
            const [lx, ly] = rotateVec(px - (t.fp_x + t.fp_w / 2), py - (t.fp_y + t.fp_h / 2), -t.fp_rot);
            const pts = t.fp_points.map((p, i) => (i === d.index
                ? [clamp(Math.round(((lx + t.fp_w / 2) / t.fp_w) * 100), 0, 100), clamp(Math.round(((ly + t.fp_h / 2) / t.fp_h) * 100), 0, 100)]
                : p));
            patchKey(t.key, { fp_points: pts });
        }
    };

    const stopDrag = () => {
        // Tərpənib heç nə dəyişməyibsə boş addım qalmasın
        const before = dragRef.current?.before;
        const toggle = dragRef.current?.toggleKeys;
        if (toggle) {
            setSelectedKeys((prev) => (toggle.every((k) => prev.includes(k))
                ? prev.filter((k) => !toggle.includes(k))
                : [...new Set([...prev, ...toggle])]));
        }
        if (before && JSON.stringify(before) === JSON.stringify(itemsRef.current)) {
            historyRef.current.undo.pop();
            syncHistorySize();
        }
        dragRef.current = null;
        setMarquee(null);
        setGuides(null);
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', stopDrag);
        window.removeEventListener('pointercancel', stopDrag);
    };

    const startDrag = (e, drag) => {
        e.stopPropagation();
        e.preventDefault();
        const [px, py] = toLogical(e);
        dragRef.current = { ...drag, px, py };
        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', stopDrag);
        window.addEventListener('pointercancel', stopDrag);
    };

    useEffect(() => stopDrag, []); // eslint-disable-line react-hooks/exhaustive-deps

    const moveDrag = (keys, extra) => ({
        mode: 'move',
        group: placed.filter((it) => keys.includes(it.key)),
        others: placed.filter((it) => !keys.includes(it.key)),
        ...extra,
    });

    // Windows qovluğu kimi: Ctrl+klik əlavə edir/çıxarır, seçilmiş masanı çəkəndə hamısı gedir.
    // Ctrl basılı çəkəndə isə masa yapışmadan, sərbəst hərəkət edir. Alt+klik: qrupdan yalnız bu üzv.
    const handleItemDown = (e, t) => {
        const own = e.altKey ? [t.key] : withGroups([t.key]);
        if (e.ctrlKey || e.metaKey) {
            startDrag(e, moveDrag([...selectedKeys, ...own], { toggleKeys: own }));
            return;
        }
        const keys = selectedKeys.includes(t.key) ? selectedKeys : [t.key, ...own.filter((k) => k !== t.key)];
        select(keys);
        startDrag(e, moveDrag(keys));
    };

    const handleCanvasDown = (e) => {
        const add = e.ctrlKey || e.metaKey;
        if (!add) select([]);
        if (e.pointerType === 'touch') return; // barmaqla boş yer səhifəni sürüşdürür
        startDrag(e, { mode: 'marquee', candidates: placed, base: add ? selectedKeys : [] });
    };

    const addVertex = () => {
        const pts = single.fp_points;
        if (pts.length >= 24) return;
        // Ən uzun tərəfin ortasına əlavə olunur
        let best = 0;
        let bestLen = -1;
        pts.forEach((p, i) => {
            const n = pts[(i + 1) % pts.length];
            const len = Math.hypot(n[0] - p[0], n[1] - p[1]);
            if (len > bestLen) { bestLen = len; best = i; }
        });
        const a = pts[best];
        const b = pts[(best + 1) % pts.length];
        const next = [...pts];
        next.splice(best + 1, 0, [Math.round((a[0] + b[0]) / 2), Math.round((a[1] + b[1]) / 2)]);
        patchKey(single.key, { fp_points: next });
        setSelectedVertex(best + 1);
    };

    const removeVertex = () => {
        const pts = single.fp_points;
        if (pts.length <= 3) return;
        const index = selectedVertex ?? pts.length - 1;
        patchKey(single.key, { fp_points: pts.filter((_, i) => i !== index) });
        setSelectedVertex(null);
    };

    // Delete: seçilənlər plandan çıxır. Xüsusi formada nöqtə seçilibsə həmin nöqtə silinir.
    // Ctrl+A: otaqda hamısı. Ctrl+Z / Ctrl+Y: geri, irəli. Ctrl+C / Ctrl+V: formanı kopyala. Ctrl+G: qrupla, qrupu aç (Ctrl+Shift+G da açır).
    // Yazı sahəsində brauzerin öz davranışı qalır.
    const isOneGroup = selection.length > 1 && !!selection[0].fp_group
        && selection.every((t) => t.fp_group === selection[0].fp_group);
    useEffect(() => {
        const onKey = (e) => {
            if (e.target.closest?.('input, textarea, select, [contenteditable="true"]')) return;
            if (e.ctrlKey || e.metaKey) {
                const k = e.key.toLowerCase();
                if (k === 'a') { e.preventDefault(); select(placed.map((t) => t.key)); }
                if (k === 'z' && e.shiftKey) { e.preventDefault(); redo(); } else if (k === 'z') { e.preventDefault(); undo(); }
                if (k === 'y') { e.preventDefault(); redo(); }
                if (k === ']') { e.preventDefault(); moveLayers('up'); }
                if (k === '[') { e.preventDefault(); moveLayers('down'); }
                if (k === 'c') { e.preventDefault(); copySelection(); }
                if (k === 'v') { e.preventDefault(); pasteShapes(); }
                if (k === 'g') {
                    e.preventDefault();
                    if (e.shiftKey || isOneGroup) ungroupSelection();
                    else groupSelection();
                }
                return;
            }
            if (e.key === 'Escape') { select([]); return; }
            if (e.key !== 'Delete' || !selection.length) return;
            e.preventDefault();
            if (single?.fp_shape === 'polygon' && selectedVertex !== null) removeVertex();
            else removeSelection();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });

    const save = async () => {
        setSaving(true);
        try {
            const placement = (t) => ({
                x: t.fp_x, y: t.fp_y, w: t.fp_w, h: t.fp_h, rot: t.fp_rot, shape: t.fp_shape, points: t.fp_points, group: t.fp_group, z: t.fp_z,
            });
            const res = await api.post('/tables/floor-plan', {
                items: items.filter((t) => t.kind === 'table').map((t) => ({ id: t.id, ...placement(t) })),
                shapes: items.filter((t) => t.kind === 'decor' || (t.kind === 'room' && isPlaced(t)))
                    .map((t) => ({
                        room_id: t.room_id,
                        fill: t.fill,
                        label: t.label,
                        overview: Number(t.fp_overview) ? 1 : 0,
                        link_room: t.link_room,
                        ...placement(t),
                    })),
            });
            toast.success(res.data?.msg || 'Yadda saxlanıldı.');
            setDirty(false);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Yadda saxlanılmadı.');
        } finally {
            setSaving(false);
        }
    };

    const goBack = async () => {
        if (dirty) {
            const ok = await confirm({
                title: 'Dəyişikliklər yadda saxlanmayıb',
                message: 'Çıxsanız etdiyiniz dəyişikliklər itəcək.',
                confirmText: 'Çıx',
                cancelText: 'Qal',
            });
            if (!ok) return;
        }
        navigate('/admin/tables');
    };

    if (!loading && !enabled) {
        return (
            <Layout>
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-8 text-center">
                    <div className="mb-3 text-4xl">🗺️</div>
                    <p className="font-semibold text-amber-800">Zal planı bu restoran üçün aktiv deyil.</p>
                    <p className="mt-1 text-sm text-amber-600">Pro paketdə, dəstək komandası tərəfindən aktivləşdirilir.</p>
                </div>
            </Layout>
        );
    }

    const hasDecor = selection.some((t) => t.kind !== 'table');
    const onlyDecor = selection.length > 0 && selection.every((t) => t.kind === 'decor');
    const primaryName = primary ? itemName(primary) : '';
    const allNoun = primary?.kind === 'room' ? 'Bütün zallar' : 'Otaqdakı bütün masalar';

    return (
        <Layout>
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'serif' }}>Zal planı</h1>
                    <p className="mt-1 text-sm text-gray-500">Çoxlu seçim: boş yerdən çərçivə çəkin və ya Ctrl ilə masalara basın. Masa yaxın masalara yapışır, Ctrl basılı çəkəndə sərbəst gedir.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={undo}
                        disabled={!historySize.undo}
                        title="Geri al (Ctrl+Z)"
                        aria-label="Geri al"
                        className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-bold text-gray-600 hover:border-primary-300 disabled:opacity-40"
                    >
                        ↶
                    </button>
                    <button
                        onClick={redo}
                        disabled={!historySize.redo}
                        title="İrəli al (Ctrl+Y)"
                        aria-label="İrəli al"
                        className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-bold text-gray-600 hover:border-primary-300 disabled:opacity-40"
                    >
                        ↷
                    </button>
                    <button onClick={goBack} className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-bold text-gray-600 hover:border-primary-300">
                        ← Masalar
                    </button>
                    <button
                        onClick={save}
                        disabled={saving || !dirty}
                        className="rounded-xl px-4 py-2 text-sm font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50"
                        style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                    >
                        {saving ? 'Saxlanılır...' : 'Yadda saxla'}
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex h-48 items-center justify-center text-gray-400">Yüklənir...</div>
            ) : tabs.length <= 1 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-white p-16 text-center text-gray-500">
                    Əvvəlcə masa əlavə edin.
                </div>
            ) : (
                <>
                    <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
                        {tabs.map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => { setPicked(tab.key); select([]); }}
                                className={`flex-shrink-0 whitespace-nowrap rounded-xl px-4 py-2 text-sm font-bold transition-all ${
                                    active === tab.key ? 'text-white shadow-md' : 'border border-gray-200 bg-white text-gray-600 hover:border-primary-300'
                                }`}
                                style={active === tab.key ? { background: 'linear-gradient(135deg, #006f91, #0090bb)' } : {}}
                            >
                                {tab.name}
                            </button>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_280px]">
                        <div className="min-w-0">
                            <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white" data-no-drag-scroll>
                                <div
                                    ref={canvasRef}
                                    onPointerDown={handleCanvasDown}
                                    className="relative min-w-[720px] select-none"
                                    style={{
                                        aspectRatio: `${CANVAS_W} / ${CANVAS_H}`,
                                        backgroundImage: 'radial-gradient(#d7dee6 1px, transparent 1px)',
                                        backgroundSize: `${(50 / CANVAS_W) * 100}% ${(50 / CANVAS_H) * 100}%`,
                                    }}
                                >
                                    {placed.map((t) => {
                                        const isSel = selectedKeys.includes(t.key);
                                        const isDecor = t.kind !== 'table';
                                        const showHandles = isSel && single?.key === t.key;
                                        return (
                                            <div
                                                key={t.key}
                                                onPointerDown={(e) => handleItemDown(e, t)}
                                                className="absolute cursor-move"
                                                style={{ ...tableBoxStyle(t), touchAction: 'none', zIndex: zOf.get(t.key) }}
                                            >
                                                {isDecor ? (
                                                    <>
                                                        <FloorShape table={t} fill={t.fill} stroke={isSel ? '#0090bb' : 'rgba(0,0,0,.15)'} strokeWidth={isSel ? 3 : 1} />
                                                        <FloorLabel table={{ ...t, name: t.label || t.name }} scale={scale} size={18} color={readableText(t.fill)} />
                                                    </>
                                                ) : (
                                                    <>
                                                        <FloorShape table={t} fill={isSel ? '#e0f4fb' : '#f1f5f9'} stroke={isSel ? '#0090bb' : '#94a3b8'} strokeWidth={isSel ? 3 : 2} />
                                                        <FloorLabel table={t} scale={scale} sub={`${t.capacity || 0} nəfər`} color="#1f2937" />
                                                    </>
                                                )}
                                                {isSel && <div className="pointer-events-none absolute inset-0 border border-dashed border-primary-400" />}
                                                {showHandles && (
                                                    <>
                                                        {RESIZE_HANDLES.map((r) => (
                                                            <div
                                                                key={`${r.hx}${r.hy}`}
                                                                title="Ölçünü dəyiş"
                                                                onPointerDown={(e) => startDrag(e, { mode: 'resize', item: t, hx: r.hx, hy: r.hy })}
                                                                className="floor-handle absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-sm border-2 border-white bg-primary-600 shadow"
                                                                style={{ left: r.left, top: r.top, cursor: r.cursor, touchAction: 'none' }}
                                                            />
                                                        ))}
                                                        {t.fp_shape === 'polygon' && t.fp_points.map((p, i) => (
                                                            <div
                                                                key={i}
                                                                onPointerDown={(e) => { setSelectedVertex(i); startDrag(e, { mode: 'vertex', item: t, index: i }); }}
                                                                className={`absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full border-2 border-white shadow ${selectedVertex === i ? 'bg-red-500' : 'bg-primary-600'}`}
                                                                style={{ left: `${p[0]}%`, top: `${p[1]}%`, touchAction: 'none' }}
                                                            />
                                                        ))}
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {isOneGroup && (() => {
                                        const b = unionBox(selection.map(boxOf));
                                        return (
                                            <div
                                                className="pointer-events-none absolute rounded border-2 border-dashed border-amber-500"
                                                style={{
                                                    left: `${(b.l / CANVAS_W) * 100}%`,
                                                    top: `${(b.t / CANVAS_H) * 100}%`,
                                                    width: `${((b.r - b.l) / CANVAS_W) * 100}%`,
                                                    height: `${((b.b - b.t) / CANVAS_H) * 100}%`,
                                                    zIndex: OVERLAY_Z,
                                                }}
                                            />
                                        );
                                    })()}
                                    {guides && (
                                        <svg
                                            className="pointer-events-none absolute inset-0 h-full w-full"
                                            viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
                                            style={{ zIndex: OVERLAY_Z }}
                                        >
                                            {guides.lines.map((g) => (g.axis === 'x'
                                                ? <line key={`l${g.axis}${g.pos}`} x1={g.pos} x2={g.pos} y1={g.from} y2={g.to} stroke={GUIDE} strokeWidth={1 / scale} />
                                                : <line key={`l${g.axis}${g.pos}`} y1={g.pos} y2={g.pos} x1={g.from} x2={g.to} stroke={GUIDE} strokeWidth={1 / scale} />))}
                                            {guides.gaps.map((g) => {
                                                const color = g.equal ? GUIDE : '#64748b';
                                                const mid = (g.from + g.to) / 2;
                                                const [x1, y1, x2, y2, tx, ty] = g.axis === 'x'
                                                    ? [g.from, g.cross, g.to, g.cross, mid, g.cross]
                                                    : [g.cross, g.from, g.cross, g.to, g.cross, mid];
                                                return (
                                                    <g key={`g${g.axis}${g.from}${g.to}${g.cross}`}>
                                                        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={1.5 / scale} />
                                                        <rect x={tx - 22 / scale} y={ty - 9 / scale} width={44 / scale} height={18 / scale} rx={4 / scale} fill={color} />
                                                        <text x={tx} y={ty} fill="#fff" fontSize={12 / scale} fontWeight="700" textAnchor="middle" dominantBaseline="central">{g.gap}px</text>
                                                    </g>
                                                );
                                            })}
                                        </svg>
                                    )}
                                    {marquee && (
                                        <div
                                            className="pointer-events-none absolute border border-primary-500 bg-primary-500/10"
                                            style={{
                                                left: `${(marquee.x1 / CANVAS_W) * 100}%`,
                                                top: `${(marquee.y1 / CANVAS_H) * 100}%`,
                                                width: `${((marquee.x2 - marquee.x1) / CANVAS_W) * 100}%`,
                                                height: `${((marquee.y2 - marquee.y1) / CANVAS_H) * 100}%`,
                                                zIndex: OVERLAY_Z,
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                        <aside className="flex flex-col gap-4">
                            <button
                                onClick={addDecor}
                                className="rounded-xl border-2 border-dashed border-primary-300 bg-white py-2.5 text-sm font-bold text-primary-700 hover:bg-primary-50"
                            >
                                + Forma əlavə et
                            </button>

                            {layers.length > 0 && (
                                <div className="rounded-2xl border border-gray-200 bg-white p-4">
                                    <div className="mb-2 flex items-center justify-between gap-2">
                                        <h3 className="text-sm font-bold text-gray-800">Layerlər</h3>
                                        <div className="flex gap-1">
                                            {[['front', '⤒', 'Ən öndə'], ['up', '↑', 'Bir addım önə (Ctrl+])'], ['down', '↓', 'Bir addım arxaya (Ctrl+[)'], ['back', '⤓', 'Ən arxada']].map(([dir, icon, title]) => (
                                                <button
                                                    key={dir}
                                                    type="button"
                                                    title={title}
                                                    aria-label={title}
                                                    disabled={!selection.length}
                                                    onClick={() => moveLayers(dir)}
                                                    className="h-6 w-6 rounded-md border border-gray-200 text-xs font-bold text-gray-600 hover:border-primary-300 disabled:opacity-40"
                                                >
                                                    {icon}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <p className="mb-2 text-[11px] leading-snug text-gray-400">Sürüşdürün: yuxarıdakı öndə, aşağıdakı arxada görünür.</p>
                                    <div className="flex max-h-72 flex-col overflow-y-auto">
                                        {layers.map((t) => {
                                            const on = selectedKeys.includes(t.key);
                                            const isOver = layerDrag?.over === t.key;
                                            return (
                                                <div
                                                    key={t.key}
                                                    data-layer={t.key}
                                                    draggable
                                                    onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setLayerDrag({ key: t.key }); }}
                                                    onDragEnd={() => setLayerDrag(null)}
                                                    onDragOver={(e) => {
                                                        e.preventDefault();
                                                        const r = e.currentTarget.getBoundingClientRect();
                                                        const after = e.clientY > r.top + r.height / 2;
                                                        setLayerDrag((d) => (d ? { ...d, over: t.key, after } : d));
                                                    }}
                                                    onDrop={(e) => { e.preventDefault(); dropLayer(t.key, layerDrag?.after); }}
                                                    onClick={(e) => handleLayerClick(e, t)}
                                                    className={`flex cursor-grab items-center gap-2 border-y-2 px-2 py-1.5 text-xs font-semibold ${
                                                        on ? 'rounded-lg bg-primary-50 text-primary-700' : 'text-gray-600 hover:bg-gray-50'
                                                    } ${isOver && !layerDrag.after ? 'border-t-primary-500' : 'border-t-transparent'} ${isOver && layerDrag.after ? 'border-b-primary-500' : 'border-b-transparent'}`}
                                                >
                                                    <span className="text-gray-400">⠿</span>
                                                    <span
                                                        className="h-3 w-3 flex-none rounded-sm border border-gray-300"
                                                        style={{ background: t.kind === 'table' ? '#f1f5f9' : t.fill }}
                                                    />
                                                    <span className="truncate">{itemName(t)}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {unplaced.length > 0 && (
                                <div className="rounded-2xl border border-gray-200 bg-white p-4">
                                    <div className="mb-2 flex items-center justify-between gap-2">
                                        <h3 className="text-sm font-bold text-gray-800">{active === OVERVIEW ? 'Planda olmayan zallar' : 'Planda olmayan masalar'} ({unplaced.length})</h3>
                                        <button onClick={placeAll} className="rounded-lg bg-primary-50 px-2 py-1 text-xs font-bold text-primary-700 hover:bg-primary-100">
                                            Hamısını yerləşdir
                                        </button>
                                    </div>
                                    <div className="flex max-h-44 flex-wrap gap-2 overflow-y-auto">
                                        {unplaced.map((t) => (
                                            <button
                                                key={t.key}
                                                onClick={() => placeTable(t)}
                                                className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:border-primary-400 hover:bg-primary-50"
                                            >
                                                + {t.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {primary ? (
                                <div className="rounded-2xl border border-gray-200 bg-white p-4">
                                    <h3 className="mb-3 truncate text-sm font-bold text-gray-800">
                                        {selection.length > 1 ? `${selection.length} seçilib. Nümunə: ${primaryName}` : primaryName}
                                    </h3>

                                    {(selection.length > 1 || (primary.kind !== 'decor' && sameKind.length > 1)) && (
                                        <div className="mb-3 flex flex-col gap-1.5">
                                            <button
                                                onClick={() => applySameSize()}
                                                className="w-full rounded-lg bg-primary-50 px-2 py-2 text-xs font-bold text-primary-700 hover:bg-primary-100"
                                            >
                                                {selection.length > 1 ? `Hamısı ${primaryName} ölçüsündə olsun` : `${allNoun} bu ölçüdə olsun`}
                                            </button>
                                            <button
                                                onClick={() => applySameSize(true)}
                                                className="w-full rounded-lg bg-primary-50 px-2 py-2 text-xs font-bold text-primary-700 hover:bg-primary-100"
                                            >
                                                {selection.length > 1 ? `Hamısı ${primaryName} forma və ölçüsündə olsun` : `${allNoun} bu forma və ölçüdə olsun`}
                                            </button>
                                        </div>
                                    )}

                                    {selection.length > 1 && (
                                        <button
                                            onClick={isOneGroup ? ungroupSelection : groupSelection}
                                            className="mb-3 w-full rounded-lg border border-gray-200 px-2 py-2 text-xs font-bold text-gray-700 hover:border-primary-300"
                                        >
                                            {isOneGroup ? 'Qrupu aç (Ctrl+G)' : 'Qrupla (Ctrl+G)'}
                                        </button>
                                    )}

                                    {selection.length > 1 && (
                                        <div className="mb-3">
                                            <div className="mb-1 text-xs font-semibold text-gray-500">Düzləndir</div>
                                            <div className="grid grid-cols-3 gap-1.5">
                                                {ALIGN_BUTTONS.map((b) => (
                                                    <button
                                                        key={b.mode}
                                                        onClick={() => applyOffsets(alignItems(selection, b.mode))}
                                                        className="rounded-lg border border-gray-200 px-1 py-1.5 text-xs font-semibold text-gray-600 hover:border-primary-300"
                                                    >
                                                        {b.label}
                                                    </button>
                                                ))}
                                            </div>
                                            {selection.length > 2 && (
                                                <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                                                    <button onClick={() => applyOffsets(distributeItems(selection, 'x'))} className="rounded-lg border border-gray-200 px-1 py-1.5 text-xs font-semibold text-gray-600 hover:border-primary-300">
                                                        Bərabər aralıq ↔
                                                    </button>
                                                    <button onClick={() => applyOffsets(distributeItems(selection, 'y'))} className="rounded-lg border border-gray-200 px-1 py-1.5 text-xs font-semibold text-gray-600 hover:border-primary-300">
                                                        Bərabər aralıq ↕
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {single && single.kind !== 'table' && (
                                        <label className="mb-3 block">
                                            <span className="mb-1 block text-xs font-semibold text-gray-500">Yazı</span>
                                            <input
                                                type="text"
                                                maxLength={60}
                                                value={single.label || ''}
                                                onChange={(e) => patchKey(single.key, { label: e.target.value }, 'label')}
                                                placeholder={single.name || 'Məs: Göl'}
                                                className="w-full rounded-xl border-2 border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
                                            />
                                        </label>
                                    )}

                                    {hasDecor && (
                                        <div className="mb-3">
                                            <div className="mb-1 text-xs font-semibold text-gray-500">Rəng</div>
                                            <div className="flex flex-wrap items-center gap-1.5">
                                                {DECOR_COLORS.map((c) => (
                                                    <button
                                                        key={c}
                                                        type="button"
                                                        aria-label={c}
                                                        onClick={() => patchSelection((t) => (t.kind !== 'table' ? { fill: c } : null))}
                                                        className={`h-7 w-7 rounded-lg border-2 ${primary.fill === c ? 'border-primary-600' : 'border-gray-200'}`}
                                                        style={{ background: c }}
                                                    />
                                                ))}
                                                <input
                                                    type="color"
                                                    aria-label="Başqa rəng"
                                                    value={primary.fill || DECOR_COLORS[0]}
                                                    onChange={(e) => patchSelection((t) => (t.kind !== 'table' ? { fill: e.target.value } : null), 'fill')}
                                                    className="h-7 w-9 cursor-pointer rounded-lg border-2 border-gray-200 bg-white p-0"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="mb-1 text-xs font-semibold text-gray-500">Forma</div>
                                    <div className="mb-3 grid grid-cols-2 gap-1.5">
                                        {SHAPE_OPTIONS.map((o) => {
                                            const on = activeShapeKey(primary) === o.key;
                                            return (
                                                <button
                                                    key={o.key}
                                                    onClick={() => patchSelection((t) => o.patch(t))}
                                                    className={`rounded-lg border px-2 py-1.5 text-xs font-semibold transition-all ${on ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-600 hover:border-primary-300'}`}
                                                >
                                                    {o.label}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {single?.fp_shape === 'polygon' && (
                                        <div className="mb-3 rounded-xl bg-gray-50 p-2.5">
                                            <p className="mb-2 text-[11px] leading-snug text-gray-500">Forma dəyişmək üçün üzərindəki nöqtələri çəkin.</p>
                                            <div className="flex gap-1.5">
                                                <button onClick={addVertex} className="flex-1 rounded-lg border border-gray-200 bg-white py-1.5 text-xs font-semibold text-gray-600 hover:border-primary-300">+ Nöqtə</button>
                                                <button onClick={removeVertex} disabled={single.fp_points.length <= 3} className="flex-1 rounded-lg border border-gray-200 bg-white py-1.5 text-xs font-semibold text-gray-600 hover:border-primary-300 disabled:opacity-50">− Nöqtə</button>
                                            </div>
                                        </div>
                                    )}

                                    <div className="mb-3 grid grid-cols-2 gap-2">
                                        <NumberField label="En" value={primary.fp_w} min={MIN_SIZE} max={maxW(primary)} onChange={(v) => patchSelection((t) => ({ fp_w: Math.min(v, maxW(t)) }), 'w')} />
                                        <NumberField label="Uzunluq" value={primary.fp_h} min={MIN_SIZE} max={maxH(primary)} onChange={(v) => patchSelection((t) => ({ fp_h: Math.min(v, maxH(t)) }), 'h')} />
                                    </div>

                                    <div className="mb-3">
                                        <NumberField label="Döndər (dərəcə)" value={primary.fp_rot} min={0} max={359} onChange={(v) => patchSelection(() => ({ fp_rot: v }), 'rot')} />
                                        <div className="mt-1.5 flex gap-1.5">
                                            {[0, 45, 90, 135].map((deg) => (
                                                <button key={deg} onClick={() => patchSelection(() => ({ fp_rot: deg }))} className="flex-1 rounded-lg border border-gray-200 py-1 text-xs font-semibold text-gray-600 hover:border-primary-300">
                                                    {deg}°
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <button onClick={removeSelection} className="w-full rounded-lg bg-red-50 py-2 text-xs font-bold text-red-500 hover:bg-red-100">
                                        {onlyDecor ? 'Sil' : 'Plandan çıxar'}
                                    </button>
                                </div>
                            ) : (
                                <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-4 text-center text-sm text-gray-400">
                                    Forma və ölçü üçün masanı seçin.
                                </div>
                            )}
                        </aside>
                    </div>
                </>
            )}
        </Layout>
    );
};

export default FloorPlan;
